import { checkedEnvelope, checkedInventoryResponse } from "./inventoryResponse";
import axios from "axios";
import Cookies from "js-cookie";
import { withApiError, withApiErrorResponse, apiErrorMessage } from "../utils/apiError";

const baseURL = process.env.REACT_APP_BASE_BACKEND_API_URL;
const BASE_PATH = "api/v1/meter-inventory-management";

const getAuthHeaders = () => {
  const token = Cookies.get("accessToken");
  return {
    Authorization: `Bearer ${token}`,
  };
};

// Spring binds List params from repeated `key=v`, not axios's default `key[]=v`.
const serializeParams = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((v) => search.append(key, v));
    } else {
      search.append(key, value);
    }
  });
  return search.toString();
};

// Super admins are not bound to one organization, so every scoped call has to send
// the organization and project it is looking at. Callers pass the ids resolved by
// `useInventoryScope` (selected org first, then the logged-in user's own org);
// each param is dropped when there is nothing to send.
const isSet = (value) => value !== undefined && value !== null && value !== "";

const scopeParams = ({ organizationId, projectId } = {}) => ({
  ...(isSet(organizationId) ? { organizationId } : {}),
  ...(isSet(projectId) ? { projectId } : {}),
});

export const fetchInventoryItems = async (payload = {}, { signal } = {}) => {
  try {
    const url = `${baseURL}${BASE_PATH}/items/search`;
    const response = await axios.post(url, payload, {
      headers: getAuthHeaders(),
      signal,
    });
    return response.data?.body?.value ?? response.data?.body ?? response.data;
  } catch (error) {
    if (axios.isCancel(error) || error?.name === "CanceledError" || error?.name === "AbortError") {
      throw withApiErrorResponse(error);
    }
    console.error("Error fetching inventory items:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

export const updateInventoryItem = async ({ inventoryItemId, status, storeId, groupId }) =>
  checkedInventoryResponse(await axios.post(`${baseURL}${BASE_PATH}/items/update`, {
    inventoryItemId,
    ...(isSet(status) ? { status } : {}),
    ...(isSet(storeId) ? { storeId } : {}),
    ...(isSet(groupId) ? { groupId } : {}),
  }, { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }));

// column-values returns either a list or a { value: label } map; callers always get a list.
export const toColumnValueOptions = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.values)) return payload.values;
  if (payload && typeof payload === "object") {
    return Object.entries(payload).map(([value, label]) => ({ value, label: label ?? value }));
  }
  return [];
};

export const fetchInventoryColumnValues = async (params = {}) => {
  try {
    const response = await axios.get(
      `${baseURL}${BASE_PATH}/items/column-values`,
      {
        params,
        paramsSerializer: serializeParams,
        headers: getAuthHeaders(),
      }
    );

    const body = response.data?.body;
    return toColumnValueOptions(body && typeof body === "object" && "value" in body ? body.value : body ?? response.data);
  } catch (error) {
    console.error(
      "Error fetching inventory column values:",
      error.response?.data || error.message
    );
    throw withApiErrorResponse(error);
  }
};
// The import endpoints sit under the plain `inventory` path, not the
// `meter-inventory-management` one the list/history calls use.
const INVENTORY_PATH = "api/v1/inventory";
const METER_INVENTORY_PATH = "api/v1/meter-inventory";

export const fetchInventoryStores = async (projectId) => {
  try {
    const response = await axios.get(`${baseURL}${METER_INVENTORY_PATH}/store`, {
      params: { projectId },
      headers: getAuthHeaders(),
    });
    return response.data?.body?.value ?? response.data?.body ?? response.data;
  } catch (error) {
    console.error(
      "Error fetching inventory stores:",
      error.response?.data || error.message
    );
    throw withApiErrorResponse(error);
  }
};

export const fetchInventoryColumns = async (params = {}) => {
  try {
    const response = await axios.get(
      `${baseURL}${BASE_PATH}/items/columns`,
      {
        params,
        paramsSerializer: serializeParams,
        headers: getAuthHeaders(),
      }
    );

    return response.data?.body?.value ?? response.data?.body ?? response.data;
  } catch (error) {
    console.error(
      "Error fetching inventory columns:",
      error.response?.data || error.message
    );
    throw withApiErrorResponse(error);
  }
};

// One call serves both the dry run and the real import: `validateOnly` decides
// whether the backend writes anything, so the caller validates first and then
// replays the exact same payload with the flag off.
export const uploadInventoryExcel = async (
  {
    file,
    storeId,
    projectId,
    itemType,
    uploadMode,
    organizationId,
    validateOnly = false,
    columnMappings,
  },
  { onUploadProgress, signal } = {}
) => {
  const formData = new FormData();
  formData.append("file", file);
  if (storeId !== undefined && storeId !== null && storeId !== "") {
    formData.append("storeId", storeId);
  }
  if (projectId !== undefined && projectId !== null && projectId !== "") {
    formData.append("projectId", projectId);
  }
  if (itemType) formData.append("itemType", itemType);
  if (uploadMode) formData.append("uploadMode", uploadMode);
  if (organizationId !== undefined && organizationId !== null && organizationId !== "") {
    formData.append("organizationId", organizationId);
  }
  formData.append("validateOnly", String(Boolean(validateOnly)));
  if (columnMappings && Object.keys(columnMappings).length > 0) {
    formData.append("columnMappings", JSON.stringify(columnMappings));
  }

  try {
    const response = await axios.post(`${baseURL}${INVENTORY_PATH}/excel-upload`, formData, {
      headers: getAuthHeaders(),
      onUploadProgress,
      signal,
    });
    return checkedEnvelope(response);
  } catch (error) {
    console.error("Error uploading inventory sheet:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

export const createOrUpdateInventoryItem = async (payload) => {
  try {
    const response = await axios.post(`${baseURL}${INVENTORY_PATH}/create-or-update`, payload, {
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    });
    return checkedEnvelope(response);
  } catch (error) {
    console.error("Error saving inventory item:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

export const bulkCreateOrUpdateInventoryItems = async (payload) => {
  try {
    const response = await axios.post(
      `${baseURL}${INVENTORY_PATH}/bulk-create-or-update`,
      payload,
      { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
    );
    return checkedEnvelope(response);
  } catch (error) {
    console.error("Error saving inventory items:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

// Scoped by the selected project alone -- the backend derives the organization from
// it, so this endpoint takes no organizationId.
export const fetchInventoryUploadList = async ({
  page = 0,
  size = 10,
  projectId,
  // Dry runs are hidden by default server-side; the history tab opts back in.
  includeValidateOnly = false,
  includeNotUploaded = true,
  notUploadedLimit = 50,
} = {}) => {
  try {
    const response = await axios.get(`${baseURL}${INVENTORY_PATH}/upload-list`, {
      params: {
        page,
        size,
        ...scopeParams({ projectId }),
        includeValidateOnly,
        includeNotUploaded,
        notUploadedLimit,
      },
      headers: getAuthHeaders(),
    });
    return response.data?.body?.value ?? response.data?.body ?? response.data;
  } catch (error) {
    console.error("Error fetching inventory upload list:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

// excel-upload only acknowledges the sheet and imports in the background, so both the
// dry run and the real import are followed by polling this until the run reaches a
// terminal status (COMPLETED / FAILED).
export const fetchInventoryUploadStatus = async (
  uploadId,
  { includeNotUploaded = true, notUploadedLimit = 200, organizationId, projectId } = {}
) => {
  try {
    const response = await axios.get(`${baseURL}${INVENTORY_PATH}/upload/${uploadId}`, {
      // Super admins work across organizations, so the scope has to travel with the
      // request -- the backend cannot infer it from the token for them.
      params: {
        includeNotUploaded,
        notUploadedLimit,
        ...scopeParams({ organizationId, projectId }),
      },
      headers: getAuthHeaders(),
    });
    return response.data?.body?.value ?? response.data?.body ?? response.data;
  } catch (error) {
    console.error("Error fetching inventory upload status:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

// Paged per-row outcome (rowStatus = CREATED | UPDATED | SKIPPED | FAILED). Used for the
// full reject list when the status payload reports notUploadedTruncated.
export const fetchInventoryUploadRows = async (
  uploadId,
  { page = 0, size = 50, rowStatus, organizationId, projectId } = {}
) => {
  try {
    const response = await axios.get(`${baseURL}${INVENTORY_PATH}/upload/${uploadId}/rows`, {
      params: {
        page,
        size,
        ...(rowStatus ? { rowStatus } : {}),
        ...scopeParams({ organizationId, projectId }),
      },
      headers: getAuthHeaders(),
    });
    return response.data?.body?.value ?? response.data?.body ?? response.data;
  } catch (error) {
    console.error("Error fetching inventory upload rows:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

// The upload-list/status payloads cap their inline `notUploaded` array (and flag
// `notUploadedTruncated`), so the Excel export walks the paged rows endpoint to
// collect every rejected row. `maxRows` is a safety stop for very large sheets.
export const fetchAllInventoryUploadRejectedRows = async (
  uploadId,
  { pageSize = 200, maxRows = 50000, organizationId, projectId } = {}
) => {
  const collected = [];
  for (let page = 0; collected.length < maxRows; page += 1) {
    const payload = await fetchInventoryUploadRows(uploadId, {
      page,
      size: pageSize,
      rowStatus: "FAILED",
      organizationId,
      projectId,
    });
    const items = Array.isArray(payload)
      ? payload
      : [payload?.rows, payload?.content, payload?.items, payload?.data].find((value) =>
          Array.isArray(value)
        ) || [];
    collected.push(...items);
    if (items.length < pageSize) break;
  }
  return collected.slice(0, maxRows);
};

// Takes the same filter body as items/search (page/size are ignored) and only
// queues the job: the workbook is built in the background and tracked through
// export-history. Failures arrive as HTTP 200 with a non-600 header code.
export const exportInventoryItems = async (payload) => {
  try {
    const response = await axios.post(`${baseURL}${INVENTORY_PATH}/export`, payload, {
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    });
    return checkedEnvelope(response);
  } catch (error) {
    console.error("Error starting inventory export:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

// Only super admins send organizationId; everyone else is scoped by the token.
export const fetchInventoryExportHistory = async ({ page = 0, size = 10, organizationId } = {}) => {
  try {
    const response = await axios.get(`${baseURL}${INVENTORY_PATH}/export-history`, {
      params: { page, size, ...scopeParams({ organizationId }) },
      headers: getAuthHeaders(),
    });
    return checkedEnvelope(response)?.body?.value ?? {};
  } catch (error) {
    console.error("Error fetching inventory export history:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

// Single-item move: { inventoryItemId, organizationId, toStoreId } (+ optional
// toStatus). The destination implies the kind of transfer, so there is no
// transferType field, and an item must be referenced one way only -- sending both
// an id and a serial for the same item transfers it twice on the backend.
export const transferInventoryItem = async (payload) => {
  try {
    const response = await axios.post(`${baseURL}${INVENTORY_PATH}/transfer`, payload, {
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    });
    return withApiError(response.data);
  } catch (error) {
    console.error("Error transferring inventory item:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

// Many-item move: { inventoryItemIds: [...], organizationId, toStoreId }.
export const transferInventoryItemsBulk = async (payload) => {
  try {
    const response = await axios.post(`${baseURL}${INVENTORY_PATH}/transfer/bulk`, payload, {
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    });
    return withApiError(response.data);
  } catch (error) {
    console.error("Error transferring inventory items:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

export const fetchInventoryTimeline = async (
  inventoryItemId,
  { organizationId, projectId } = {}
) => {
  try {
    const response = await axios.post(
      `${baseURL}${BASE_PATH}/timeline`,
      { inventoryItemId, ...scopeParams({ organizationId, projectId }) },
      { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
    );
    return response.data?.body?.value ?? response.data?.body ?? response.data;
  } catch (error) {
    console.error("Error fetching inventory timeline:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};

// Assign selected inventory items to a subcontractor group, including a single item.
const submitInventoryGroupAssignment = async (payload, reassign = false) => {
  const organizationId = Number(payload.organizationId);
  const requestBody = {
    inventoryItemIds: payload.inventoryItemIds,
    groupId: payload.groupId,
    projectId: payload.projectId,
    ...(!reassign && payload.expectedFromStoreId != null ? { expectedFromStoreId: payload.expectedFromStoreId } : {}),
    ...(payload.note ? { note: payload.note } : {}),
    ...(Number.isSafeInteger(organizationId) && organizationId > 0 ? { organizationId } : {}),
  };

  const response = await axios.post(
    `${baseURL}${BASE_PATH}/items/${reassign ? "reassign" : "assign"}/bulk-by-group`,
    requestBody,
    { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
  );
  // Validation errors can arrive with HTTP 200; only code 600 means success.
  const data = response.data;
  if (data?.header?.code != null && Number(data.header.code) !== 600) {
    const error = new Error(
      apiErrorMessage(data) || data?.header?.message || "Unable to assign meters to the group."
    );
    error.response = response;
    throw withApiErrorResponse(error);
  }
  return data;
};

export const assignInventoryItemsByGroup = (payload) => submitInventoryGroupAssignment(payload);
export const reassignInventoryItemsByGroup = (payload) => submitInventoryGroupAssignment(payload, true);

// Meter numbers remain strings: numeric conversion would lose leading zeroes.

const assignmentDestination = ({ toStoreIds, groupIds }) => ({
  ...(toStoreIds?.length ? { toStoreIds } : {}),
  ...(groupIds?.length ? { groupIds } : {}),
});

const submitMeterNumbers = async (action, { projectId, meterNos, ...destination }) =>
  checkedInventoryResponse(await axios.post(`${baseURL}${BASE_PATH}/${action}/meter-nos`, {
    projectId,
    meterNos: [...new Set(meterNos.map((number) => String(number).trim()).filter(Boolean))],
    ...assignmentDestination(destination),
  }, { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }));

export const assignInventoryMeterNumbers = (payload) => submitMeterNumbers("assign", payload);
export const reassignInventoryMeterNumbers = (payload) => submitMeterNumbers("reassign", payload);

export const assignInventoryExcel = async ({ file, projectId, organizationId, itemType, ...destination }) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("projectId", projectId);
  if (isSet(organizationId)) formData.append("organizationId", organizationId);
  formData.append("itemType", itemType);
  Object.entries(assignmentDestination(destination)).forEach(([key, ids]) => {
    ids.forEach((id) => formData.append(key, id));
  });
  return checkedInventoryResponse(await axios.post(`${baseURL}${BASE_PATH}/assign/excel-upload`, formData, {
    headers: getAuthHeaders(),
  }));
};

// Inventory dashboard counts: items per status, and per group the items assigned
// to it broken down by status. Only a super admin names the organization (and must);
// everyone else is pinned to their own. Errors arrive as HTTP 200 with a non-600
// header code, so the envelope is checked before body.value is read.
export const fetchInventoryDashboard = async ({ organizationId, projectId, itemType } = {}) => {
  try {
    const response = await axios.get(`${baseURL}${INVENTORY_PATH}/dashboard`, {
      params: {
        ...scopeParams({ organizationId, projectId }),
        ...(isSet(itemType) ? { itemType } : {}),
      },
      headers: getAuthHeaders(),
    });
    return checkedEnvelope(response)?.body?.value ?? {};
  } catch (error) {
    console.error("Error fetching inventory dashboard:", error.response?.data || error.message);
    throw withApiErrorResponse(error);
  }
};
